import { LayoutType, LayoutTypeLabels, Language, StatusVariant } from '@consigli/types';

import { Field, Fieldset, Select, TextInput, TextArea } from '@/components';
import { FileUpload } from '@/components/file-upload';

import { languageOptions } from './logic/utils';

interface LayoutOptimizationFormProps {
  name: string;
  layoutType: LayoutType;
  language: Language;
  comments: string;
  floorHeight: number | null;
  files: File[];
  errors?: Record<string, string>;
  onNameChange: (value: string) => void;
  onLayoutTypeChange: (value: LayoutType) => void;
  onLanguageChange: (value: Language) => void;
  onCommentsChange: (value: string) => void;
  onFloorHeightChange: (value: number | null) => void;
  onFilesChange: (files: File[]) => void;
}

/**
 * Form for creating a layout optimization.
 * @param name - The name of the layout optimization.
 * @param layoutType - The type of the layout optimization.
 * @param language - The language of the layout optimization.
 * @param comments - The comments of the layout optimization.
 * @param floorHeight - The floor height of the layout optimization.
 * @param files - The files of the layout optimization.
 * @param errors - The errors of the layout optimization.
 * @param onNameChange - The function to call when the name is changed.
 * @param onLayoutTypeChange - The function to call when the layout type is changed.
 * @param onLanguageChange - The function to call when the language is changed.
 * @param onCommentsChange - The function to call when the comments are changed.
 * @param onFloorHeightChange - The function to call when the floor height is changed.
 * @param onFilesChange - The function to call when the files are changed.
 * @returns The layout optimization form.
 */
export const LayoutOptimizationForm = ({
  name,
  layoutType,
  language,
  comments,
  floorHeight,
  files,
  errors = {},
  onNameChange,
  onLayoutTypeChange,
  onLanguageChange,
  onCommentsChange,
  onFloorHeightChange,
  onFilesChange,
}: LayoutOptimizationFormProps) => {
  const layoutTypeOptions = Object.entries(LayoutTypeLabels).map(([value, label]) => ({
    label,
    value: value as LayoutType,
  }));

  return (
    <Fieldset legend="Layout Optimization">
      <p className="text-sm text-gray-500">
        Please fill in the details to add a new layout optimization to the project
      </p>

      <Field label="Name" htmlFor="name-input" required error={errors.name}>
        <TextInput
          placeholder="Please enter a layout optimization name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          className="w-full"
        />
      </Field>

      <Field label="Layout Type" htmlFor="layout-type-select" required error={errors.packageType}>
        <Select
          options={layoutTypeOptions}
          placeholder="Select Layout Type"
          value={layoutTypeOptions.find((opt) => opt.value === layoutType)}
          onValueChange={(option) => onLayoutTypeChange(option.value as LayoutType)}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => String(option.value)}
          className="w-full"
        />
      </Field>

      {layoutType === LayoutType.REPORTS && (
        <Field label="Language" htmlFor="language-select" error={errors.language}>
          <Select
            options={languageOptions}
            placeholder="Select Language"
            value={languageOptions.find((opt) => opt.value === language)}
            onValueChange={(option) => onLanguageChange(option.value as Language)}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => String(option.value)}
            className="w-full"
          />
        </Field>
      )}

      <Field label="Comments" htmlFor="comments-input" error={errors.comments}>
        <TextArea
          placeholder="Enter any additional comments or requirements"
          value={comments}
          onChange={(e) => onCommentsChange(e.target.value)}
          className="w-full"
          rows={3}
        />
      </Field>

      {layoutType === LayoutType.PLANT_ROOM && (
        <Field
          label="Floor Height (m)"
          htmlFor="floor-height-input"
          required
          error={errors.floorHeight}
        >
          <TextInput
            placeholder="Enter floor height in meters"
            value={floorHeight?.toString() || ''}
            onChange={(e) =>
              onFloorHeightChange(e.target.value ? parseFloat(e.target.value) : null)
            }
            className="w-full"
          />
        </Field>
      )}
      <Field label="Files" htmlFor="files-input" required error={errors.files}>
        <FileUpload
          setFiles={onFilesChange}
          files={files}
          alert={{
            title: 'Necessary files',
            description:
              'This service requires an Architectural model (.ifc) and 3D model of technical equipment and components (.ifc)',
            variant: StatusVariant.INFO,
          }}
          acceptedFileFormats={['.ifc']}
        />
      </Field>
    </Fieldset>
  );
};
